import { signL1Action } from '@nktkas/hyperliquid/signing';
import { pickActiveEvmWallet, connectEvmWallet } from './detectEvmWallets';
import { EVM_WALLET_SIGN_MSG, evmWalletMismatchMsg } from './evmWalletMessages';
import { tryHlL1SignNetworkSwitch, isChainMismatchError } from './hlSignNetwork';
import { createEip1193EthersWallet, formatL1SignError } from './hlWalletAdapter';

/**
 * Sign Hyperliquid L1 order action with the user's EVM browser wallet.
 * MetaMask must be on chain 1337 (HL L1 signing) — not Arbitrum One or HL user-sign (421614).
 */
export async function signHyperliquidL1Action(provider, fromAddress, action, nonce, { isTestnet = false } = {}) {
  if (!provider?.request) throw new Error('Wallet does not support signing');

  await tryHlL1SignNetworkSwitch(provider);

  const wallet = createEip1193EthersWallet(provider, fromAddress);
  const sign = () => signL1Action({ wallet, action, nonce, isTestnet });

  try {
    return await sign();
  } catch (e) {
    if (isChainMismatchError(e)) {
      await tryHlL1SignNetworkSwitch(provider);
      try {
        return await sign();
      } catch (retryErr) {
        throw new Error(formatL1SignError(retryErr));
      }
    }
    throw new Error(formatL1SignError(e));
  }
}

export async function submitManualOrderWithBrowserWallet({ walletAddress, signalId, apiBase, authHeaders }) {
  const w = await pickActiveEvmWallet();
  if (!w) throw new Error(EVM_WALLET_SIGN_MSG);

  const { address } = await connectEvmWallet(w.provider);
  if (
    walletAddress &&
    address.toLowerCase() !== String(walletAddress).trim().toLowerCase()
  ) {
    throw new Error(evmWalletMismatchMsg(address));
  }

  const axios = (await import('axios')).default;
  const { data: prepared } = await axios.post(
    `${apiBase}/multi-asset/signals/${signalId}/prepare-order`,
    {},
    { headers: authHeaders() }
  );

  const signature = await signHyperliquidL1Action(
    w.provider,
    address,
    prepared.action,
    prepared.nonce
  );

  const { data: submitted } = await axios.post(
    `${apiBase}/multi-asset/signals/${signalId}/submit-order`,
    {
      action: prepared.action,
      nonce: prepared.nonce,
      signature,
    },
    { headers: authHeaders() }
  );

  return submitted;
}
